import type { Dispatch, PropsWithChildren, SetStateAction } from 'react';
import { createContext, useEffect, useState } from 'react';
import { useContextOrThrow, useRefWrappedCallback } from '@elfsight/lib-utils';
import { noop } from 'es-toolkit';
import type {
  PaymentProcessingChangeHandler,
  PaymentSessionStatus
} from '../common';

export type PaymentSessionMetadata = {
  sessionId: string | undefined;
  setSessionId: Dispatch<SetStateAction<string | undefined>>;
  sessionStatus: PaymentSessionStatus;
  setSessionStatus: Dispatch<SetStateAction<PaymentSessionStatus>>;
  isPaymentInProcess: boolean;
  setIsPaymentInProcess: Dispatch<SetStateAction<boolean>>;
};

const PaymentSessionMetadataContext =
  createContext<PaymentSessionMetadata | null>(null);

export function PaymentSessionMetadataProvider({
  onPaymentProcessing = noop,
  children
}: PropsWithChildren<{
  onPaymentProcessing?: PaymentProcessingChangeHandler;
}>) {
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [sessionStatus, setSessionStatus] =
    useState<PaymentSessionStatus>('unset');
  const [isPaymentInProcess, setIsPaymentInProcess] = useState(false);
  const stableOnPaymentProcessing = useRefWrappedCallback(onPaymentProcessing);

  useEffect(() => {
    stableOnPaymentProcessing(isPaymentInProcess);
  }, [isPaymentInProcess, stableOnPaymentProcessing]);

  return (
    <PaymentSessionMetadataContext.Provider
      value={{
        sessionId,
        setSessionId,
        sessionStatus,
        setSessionStatus,
        isPaymentInProcess,
        setIsPaymentInProcess
      }}
    >
      {children}
    </PaymentSessionMetadataContext.Provider>
  );
}

export function usePaymentSessionMetadata() {
  return useContextOrThrow(
    PaymentSessionMetadataContext,
    'PaymentSessionMetadata'
  );
}
